import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL = import.meta.env.VITE_GIFTCARD_SOCKET_URL || 'http://localhost:9114';
const SOCKET_TOKEN = import.meta.env.VITE_GIFTCARD_SOCKET_TOKEN || '';
const SOCKET_CHANNEL = import.meta.env.VITE_GIFTCARD_SOCKET_CHANNEL || 'giftcard.orders';
const SOCKET_EVENTS = ['order.created', 'order.updated', 'giftcard.redeemed'];

export default function useCardRedemptionSocket(onMessage) {
  const onMessageRef = useRef(onMessage);
  const socketRef = useRef(null);
  const subscribedChannelRef = useRef('');
  const [status, setStatus] = useState('connecting');

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    const socket = io(SOCKET_URL, {
      autoConnect: true,
      auth: {
        token: SOCKET_TOKEN,
      },
    });

    socketRef.current = socket;
    setStatus('connecting');

    const subscribeToChannel = () => {
      socket.emit('subscribe', { channel: SOCKET_CHANNEL }, (response) => {
        if (response?.status) {
          subscribedChannelRef.current = response.channel || SOCKET_CHANNEL;
          setStatus('connected');
          return;
        }

        subscribedChannelRef.current = '';
        setStatus('error');
      });
    };

    const handleMessage = (eventName) => (payload) => {
      if (onMessageRef.current) {
        onMessageRef.current(payload, { eventName, channel: SOCKET_CHANNEL });
      }
    };

    socket.on('connect', subscribeToChannel);
    socket.on('disconnect', () => {
      subscribedChannelRef.current = '';
      setStatus('disconnected');
    });
    socket.on('connect_error', () => {
      subscribedChannelRef.current = '';
      setStatus('error');
    });

    const listenerMap = new Map();

    SOCKET_EVENTS.forEach((eventName) => {
      const listener = handleMessage(eventName);
      listenerMap.set(eventName, listener);
      socket.on(eventName, listener);
    });

    return () => {
      if (subscribedChannelRef.current) {
        socket.emit('unsubscribe', { channel: subscribedChannelRef.current });
      }

      SOCKET_EVENTS.forEach((eventName) => {
        socket.off(eventName, listenerMap.get(eventName));
      });

      socket.off('connect', subscribeToChannel);
      socket.off('disconnect');
      socket.off('connect_error');
      socket.disconnect();
      socketRef.current = null;
      subscribedChannelRef.current = '';
    };
  }, []);

  return status;
}
